import { readStaffTenantSlug, resolveTenantHeader, type ApiErrorResponse } from "@l30/shared";

let _getToken: (() => Promise<string | null>) | null = null;

/**
 * Set the auth token getter (called from ClerkProvider setup).
 */
export function setAuthTokenGetter(fn: () => Promise<string | null>) {
  _getToken = fn;
}

// Structural type for Zod-like schemas. Used instead of `import type
// { ZodType }` because the workspace has zod v3 (shared) and v4 (web)
// installed side-by-side, and the v4 generic returns `unknown` for
// schemas typed by v3. The structural `safeParse` signature carries
// the inferred type through cleanly across both versions.
interface ResponseSchema<T> {
  safeParse(data: unknown):
    | { success: true; data: T }
    | {
        success: false;
        error: { issues: ReadonlyArray<{ path: PropertyKey[]; message: string }> };
      };
}

/**
 * Per-call options on top of `RequestInit`. Used by callers that need
 * to override the implicit tenant resolution.
 */
export interface ApiFetchOptions extends RequestInit {
  /**
   * Send `X-Tenant-ID: <slug>` on this call, regardless of the staff
   * app's host-based default. Used by the cross-tenant portal-admin
   * surfaces (e.g. selecting which tenant's portal users to manage).
   * Slug is validated server-side; client doesn't sanitise it.
   */
  tenantOverride?: string;
}

/**
 * The apiFetch contract — validated by default, opt-out must be explicit.
 * ------------------------------------------------------------------------
 * `apiFetch(path, options, schema)` REQUIRES a Zod-like response schema. The
 *   body is parsed against it at runtime — drift surfaces as `ApiContractError`
 *   instead of silently propagating through the UI. Canon: 04-shared/ssot.md R6.
 *   This is the default: a caller that wants a typed `T` must prove the shape.
 *
 * `apiFetchUnchecked<T>(path, options)` is the explicit, greppable opt-out for
 *   the deliberate "no schema yet" case — it casts the raw JSON to `T` with NO
 *   runtime validation (the historical no-schema behaviour). Reach for it only
 *   when there is no shared Zod schema to validate against; prefer wiring a real
 *   schema and using `apiFetch`.
 *
 * Enforcement: `apiFetch`'s required third argument makes the choice mechanical
 *   — a no-schema call fails typecheck unless it is deliberately switched to
 *   `apiFetchUnchecked`. A future eslint rule could flag `apiFetchUnchecked`
 *   usages (e.g. `no-restricted-syntax` on the CallExpression callee) to keep
 *   the opt-out list visible in review; not built today.
 */

/**
 * Minimal fetch wrapper that handles auth, content-type, and error parsing.
 * This replaces the 4,400-line monolithic API client from the old codebase.
 *
 * The `responseSchema` is REQUIRED: the response body is parsed against it at
 * runtime — drift surfaces as `ApiContractError` instead of silently
 * propagating through the UI. Canon: 04-shared/ssot.md R6. For the deliberate
 * "no schema yet" case, use `apiFetchUnchecked` (the explicit opt-out).
 */
export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions,
  responseSchema: ResponseSchema<T>,
): Promise<T> {
  const response = await apiRequest(path, options);

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const body = (await response.json()) as unknown;

  const parsed = responseSchema.safeParse(body);
  if (!parsed.success) {
    throw new ApiContractError(path, parsed.error.issues);
  }
  return parsed.data;
}

/**
 * Explicit opt-out from runtime response validation — the raw JSON is cast to
 * `T` with NO schema check (the historical no-schema behaviour of `apiFetch`).
 * Byte-for-byte the old code path; kept as a distinct, greppable name so the
 * unvalidated surface stays visible. Use only when no shared Zod schema exists
 * to validate against yet. See the apiFetch contract note above.
 */
export async function apiFetchUnchecked<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const response = await apiRequest(path, options);

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const body = (await response.json()) as unknown;
  return body as T;
}

export async function apiFetchText(
  path: string,
  options: ApiFetchOptions = {},
): Promise<{ text: string; contentDisposition: string | null }> {
  const response = await apiRequest(path, options);
  return {
    text: await response.text(),
    contentDisposition: response.headers.get("Content-Disposition"),
  };
}

async function apiRequest(path: string, options: ApiFetchOptions): Promise<Response> {
  const { tenantOverride, ...rest } = options;
  const headers = new Headers(rest.headers);
  const tenantSlug = resolveTenantHeader(tenantOverride, readStaffTenantSlug());
  if (tenantSlug) {
    headers.set("X-Tenant-ID", tenantSlug);
  }

  // Add auth token. Clerk's getToken() can return null briefly during
  // hydration even after isLoaded is true — its session refresh is
  // racy with first-render fetches. Retry a few times before giving up
  // so React Query users don't see a transient 401.
  if (_getToken) {
    let token: string | null = null;
    for (let attempt = 0; attempt < 10; attempt++) {
      try {
        token = await _getToken();
      } catch {
        token = null;
      }
      if (token) break;
      await new Promise((r) => setTimeout(r, 100));
    }
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  // Set content-type for JSON bodies. Leave FormData alone so the browser can
  // set multipart boundary (SharePoint working-doc upload — ADR-0077).
  if (rest.body && !(rest.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`/api${path}`, {
    ...rest,
    headers,
  });

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => null)) as ApiErrorResponse | null;
    const code = errorBody?.error?.code ?? "UNKNOWN";
    const message = errorBody?.error?.message ?? `API error: ${response.status}`;
    const requestId = errorBody?.error?.requestId ?? null;
    const details = errorBody?.error?.details ?? null;
    throw new ApiError(code, message, response.status, { requestId, details });
  }

  return response;
}

/** Streaming POST: same auth/tenant handling as apiFetch, but returns the raw
 *  Response so the caller can read response.body (apiFetch consumes it as JSON). */
export async function streamRequest(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<Response> {
  return apiRequest(path, { method: "POST", body: JSON.stringify(body), signal });
}

export class ApiError extends Error {
  public readonly requestId: string | null;
  public readonly details: unknown;

  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    extras: { requestId?: string | null; details?: unknown } = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.requestId = extras.requestId ?? null;
    this.details = extras.details ?? null;
  }
}

/**
 * Thrown when an API response doesn't match the expected Zod schema.
 * Canon: 04-shared/ssot.md R6 — drift surfaces here rather than silently
 * propagating through the UI. The `issues` array carries the Zod error
 * paths so support / engineering can pinpoint which field drifted.
 */
export class ApiContractError extends Error {
  constructor(
    public readonly path: string,
    public readonly issues: ReadonlyArray<{ path: PropertyKey[]; message: string }>,
  ) {
    const summary = issues
      .slice(0, 3)
      .map((i) => `${i.path.map(String).join(".") || "(root)"}: ${i.message}`)
      .join("; ");
    super(`Response from ${path} doesn't match expected shape: ${summary}`);
    this.name = "ApiContractError";
  }
}
